"use client";

import { type ImageKind } from "@milqit/shared-types";

import { cn } from "../lib/cn";
import { toneBg, type Tone } from "../lib/tones";
import { useImageLoad } from "../lib/use-image-load";
import { ImageFallback, ImageShimmer } from "./image-placeholders";

export interface ProductThumbProps {
  /** The image; also the fallback when `srcSet` is given. */
  src: string;
  /**
   * Smaller renditions for the browser to choose from (1x/2x of the thumbnail's size), built by
   * the app: this package stays framework-free, and a 44 px thumbnail shouldn't download the
   * original photo.
   */
  srcSet?: string;
  sizes?: string;
  alt: string;
  tone?: Tone;
  /** "art" illustrations are shown contained with padding; photos fill the frame. */
  kind?: ImageKind;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const frames = {
  xs: "size-8 rounded-lg",
  sm: "size-11 rounded-xl",
  md: "size-[72px] rounded-2xl",
  lg: "size-24 rounded-[20px]",
};

/** Each size's width in CSS pixels, for apps building `srcSet`. */
export const THUMB_PIXELS = { xs: 32, sm: 44, md: 72, lg: 96 } as const;

/** Small square product image used in lists, baskets and admin tables. */
export function ProductThumb({
  src,
  srcSet,
  sizes,
  alt,
  tone = "mint",
  kind,
  size = "sm",
  className,
}: ProductThumbProps) {
  const isArt = kind ? kind === "art" : src.endsWith(".svg");
  const { status, ref, onLoad, onError } = useImageLoad(src);
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden",
        toneBg[tone],
        frames[size],
        className,
      )}
    >
      {/* Illustrations are tiny and arrive at once; a sweep would only flicker. */}
      {status === "loading" && !isArt ? <ImageShimmer /> : null}
      {status === "error" ? (
        <ImageFallback label={alt} compact />
      ) : (
        <img
          ref={ref}
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={onLoad}
          onError={onError}
          className={cn("relative", isArt ? "size-3/4 object-contain" : "size-full object-cover")}
        />
      )}
    </span>
  );
}
